import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles,
  Lock,
  User,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Briefcase,
  Calendar,
  ArrowRight,
  Eye,
  EyeOff,
  Users,
  Check,
} from "lucide-react";
import { LiventyLogoMark, LiventyWordmark } from "../components/common/LiventyLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPE_LABELS = {
  casamento: "Casamento",
  aniversario: "Aniversário",
  corporativo: "Corporativo",
  formatura: "Formatura",
  evento: "Evento",
};

const ROLE_LABELS = {
  admin: "Administrador",
  editor: "Editor",
  viewer: "Visualizador",
  custom: "Permissões customizadas",
};

const formatDateBR = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso.length <= 10 ? `${iso}T12:00:00` : iso);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return null;
  }
};

/* ============================================================================
 * StaffInvitePage — /invite/staff/:token
 *
 * Tela DEDICADA para convites de colaborador (staff).
 * Visual full-screen premium estilo VIP/login Liventy:
 *  - Glassmorphism + gradient brand
 *  - Mostra organizador + eventos atribuídos
 *  - Permite criar senha + finalizar cadastro
 *  - Auto-login após concluir → /dashboard
 * ========================================================================= */
export default function StaffInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [validating, setValidating] = useState(true);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  // form
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 1️⃣ Carrega preview do convite
  useEffect(() => {
    if (!token) {
      setError("Token de convite inválido");
      setValidating(false);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/invite/staff/preview/${token}`);
        if (!res.data?.valid) {
          setError("Convite não encontrado. Verifique o link enviado pelo organizador.");
        } else if (res.data?.expired) {
          setError("Este convite expirou. Solicite um novo ao organizador.");
        } else if (res.data?.used) {
          setError("Este convite já foi utilizado. Faça login para acessar seus eventos.");
        } else {
          setPreview(res.data);
          if (res.data.name) setName(res.data.name);
        }
      } catch (err) {
        setError(err?.response?.data?.detail || "Erro ao validar convite.");
      } finally {
        setValidating(false);
      }
    })();
  }, [token]);

  // 2️⃣ Aceitar — apenas para usuários NOVOS (cria conta).
  // ⚠️ Para usuários EXISTENTES, redirecionamos para /login com staff_invite=TOKEN.
  const handleAccept = async (e) => {
    e.preventDefault();
    if (preview?.already_user) {
      // Não deveria entrar aqui — UI mostra outro botão. Mas defensive:
      handleGoLogin();
      return;
    }
    if (!name.trim() || name.trim().length < 2) {
      toast.error("Digite seu nome completo");
      return;
    }
    if (!password || password.length < 6) {
      toast.error("Senha precisa ter no mínimo 6 caracteres");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/users/accept-invite`, {
        token,
        password,
        name: name.trim() || preview.name || "",
      });
      const at = res.data.access_token;
      const user = res.data.user;
      if (at) {
        localStorage.setItem("token", at);
        axios.defaults.headers.common["Authorization"] = `Bearer ${at}`;
      }
      if (user) {
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("nosly_tour_completed", "true");
      }
      toast.success(`Bem-vindo${name ? `, ${name.split(" ")[0]}` : ""}! Acesso liberado a ${preview.wedding_count} evento${preview.wedding_count === 1 ? "" : "s"}.`);
      setTimeout(() => {
        window.location.assign("/dashboard");
      }, 700);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Falha ao aceitar convite.";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  // 🔑 Usuário existente → vai pro /login com staff_invite pra reivindicar após login
  const handleGoLogin = () => {
    const qs = new URLSearchParams({
      email: preview?.email || "",
      staff_invite: token || "",
    }).toString();
    window.location.assign(`/login?${qs}`);
  };

  // 🔁 Esqueci senha — dispara fluxo de redefinição real
  const [resetSending, setResetSending] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const handleForgotPassword = async () => {
    if (!preview?.email) return;
    setResetSending(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: preview.email });
      setResetSent(true);
      toast.success("Email de redefinição enviado!");
    } catch (err) {
      // O endpoint sempre retorna sucesso por design (não revela enumeration)
      setResetSent(true);
      toast.success("Se a conta existir, o email será enviado.");
    } finally {
      setResetSending(false);
    }
  };

  const inviterFirstName = (preview?.inviter_name || "Organizador").split(" ")[0];

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ fontFamily: "'Nunito', sans-serif", backgroundColor: "#F8F7FF" }}
      data-testid="staff-invite-page"
    >
      <style>{`
        @keyframes staffFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .staff-anim-1 { animation: staffFadeUp 0.55s cubic-bezier(.22,.9,.32,1) both; }
        .staff-anim-2 { animation: staffFadeUp 0.55s cubic-bezier(.22,.9,.32,1) 0.08s both; }
        .staff-anim-3 { animation: staffFadeUp 0.55s cubic-bezier(.22,.9,.32,1) 0.16s both; }
        @keyframes briefPulse {
          0%, 100% { transform: scale(1); filter: drop-shadow(0 8px 22px rgba(107,61,246,0.40)); }
          50% { transform: scale(1.04); filter: drop-shadow(0 12px 30px rgba(107,61,246,0.55)); }
        }
        .staff-icon { animation: briefPulse 2.6s ease-in-out infinite; }
      `}</style>

      {/* Header */}
      <header
        className="px-6 sm:px-10 py-5 flex items-center justify-between bg-white/60 backdrop-blur-sm"
        style={{ borderBottom: "1px solid #E9E5FF" }}
      >
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2.5 group"
          data-testid="staff-invite-brand-home"
        >
          <LiventyLogoMark size={32} />
          <LiventyWordmark height={22} variant="dark" />
        </button>
        <Link
          to="/login"
          className="text-[13px] font-bold transition-colors text-zinc-600 hover:text-zinc-900"
          data-testid="staff-invite-back-to-login"
        >
          Entrar
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 py-10 sm:py-14 relative overflow-hidden">
        {/* Glows brand (mesma estética premium VIP) */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: "10%",
            left: "-12%",
            width: "min(440px,55vw)",
            height: "min(440px,55vw)",
            borderRadius: "50%",
            background: "radial-gradient(50% 50% at 50% 50%, rgba(107,61,246,0.22), transparent 70%)",
            filter: "blur(60px)",
          }}
        />
        <div
          className="absolute pointer-events-none"
          style={{
            bottom: "5%",
            right: "-12%",
            width: "min(480px,55vw)",
            height: "min(480px,55vw)",
            borderRadius: "50%",
            background: "radial-gradient(50% 50% at 50% 50%, rgba(255,77,141,0.18), transparent 70%)",
            filter: "blur(60px)",
          }}
        />

        <div className="w-full max-w-[480px] relative z-10">
          {/* ── State: validating ─────────────────────── */}
          {validating && (
            <div className="staff-anim-1 text-center py-16" data-testid="staff-invite-loading">
              <Loader2 size={32} className="animate-spin mx-auto text-violet-500" />
              <p className="mt-4 text-[14px] text-zinc-500">Validando seu convite…</p>
            </div>
          )}

          {/* ── State: error ─────────────────────── */}
          {!validating && error && (
            <div
              className="staff-anim-1 bg-white rounded-3xl p-8 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.10)] border border-rose-100"
              data-testid="staff-invite-error-card"
            >
              <div className="w-16 h-16 mx-auto rounded-2xl bg-rose-50 flex items-center justify-center mb-5">
                <AlertTriangle size={28} className="text-rose-500" />
              </div>
              <h1 className="text-[22px] font-bold text-center text-zinc-900 tracking-tight">Convite indisponível</h1>
              <p className="text-center text-[14px] text-zinc-500 mt-2">{error}</p>
              <button
                onClick={() => navigate("/login")}
                className="w-full mt-6 h-12 rounded-xl bg-zinc-900 text-white text-[14px] font-bold hover:bg-zinc-800 transition-colors"
                data-testid="staff-invite-go-login"
              >
                Ir para login
              </button>
            </div>
          )}

          {/* ── State: valid → form ─────────────────────── */}
          {!validating && !error && preview && (
            <>
              {/* Briefcase header */}
              <div className="staff-anim-1 text-center mb-7">
                <div
                  className="staff-icon inline-flex items-center justify-center w-20 h-20 rounded-3xl mb-5"
                  style={{ backgroundImage: "linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 60%, #FFB347 100%)" }}
                >
                  <Briefcase size={34} className="text-white" strokeWidth={2.2} />
                </div>
                <div
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-violet-50 border border-violet-200 mb-3"
                  data-testid="staff-invite-badge"
                >
                  <Sparkles size={12} className="text-violet-600" />
                  <span className="text-[11px] font-extrabold text-violet-700 uppercase tracking-widest">
                    CONVITE EQUIPE
                  </span>
                </div>
                <h1 className="text-[26px] sm:text-[30px] tracking-tight leading-tight font-bold text-zinc-900">
                  {preview.name ? `Olá, ${preview.name.split(" ")[0]}!` : "Você foi convidado!"}
                </h1>
                <p className="text-[14px] text-zinc-500 mt-2 leading-relaxed">
                  <span className="font-bold text-zinc-900">{inviterFirstName}</span>{" "}
                  te convidou para colaborar em{" "}
                  <span className="font-bold text-zinc-900">{preview.wedding_count}</span>{" "}
                  evento{preview.wedding_count === 1 ? "" : "s"} no espaço Liventy.
                </p>
              </div>

              {/* Lista de eventos atribuídos */}
              {preview.weddings?.length > 0 && (
                <div
                  className="staff-anim-2 mb-5 bg-white rounded-2xl p-4 sm:p-5 border border-violet-100 shadow-[0_10px_30px_-12px_rgba(107,61,246,0.18)]"
                  data-testid="staff-invite-weddings"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Users size={14} className="text-violet-600" />
                    <p className="text-[11px] uppercase tracking-[0.16em] font-bold text-violet-700">
                      Eventos atribuídos · função {ROLE_LABELS[preview.role] || preview.role}
                    </p>
                  </div>
                  <div className="space-y-2">
                    {preview.weddings.map((w) => (
                      <div
                        key={w.id}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-violet-50/50 ring-1 ring-violet-100/70"
                        data-testid={`staff-invite-wedding-${w.id}`}
                      >
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-[10px] font-bold"
                          style={{ backgroundImage: "linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 60%, #FFB347 100%)" }}
                        >
                          {(w.event_name || "E")
                            .split(/[\s&]+/)
                            .filter(Boolean)
                            .slice(0, 2)
                            .map((s) => s.charAt(0).toUpperCase())
                            .join("") || "E"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-semibold text-zinc-900 truncate">{w.event_name}</p>
                          <p className="text-[10.5px] text-zinc-500">
                            {EVENT_TYPE_LABELS[w.event_type] || w.event_type || "Evento"}
                            {w.event_date && <> · <Calendar size={10} className="inline -mt-0.5" /> {formatDateBR(w.event_date)}</>}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Form */}
              <div
                className="staff-anim-3 bg-white rounded-3xl p-6 sm:p-8 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.08)] border border-violet-100"
                data-testid="staff-invite-form-card"
              >
                {/* ✅ Iter 79 — Já tem conta: mostra "Tudo pronto" + Entrar/Redefinir */}
                {preview.already_user ? (
                  <div className="space-y-5" data-testid="staff-invite-existing-user">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 size={20} className="text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="text-[17px] font-bold text-zinc-900 tracking-tight">
                          Tudo pronto!
                        </h3>
                        <p className="text-[13px] text-zinc-600 mt-1 leading-relaxed">
                          Encontramos sua conta Liventy{preview.email ? <> com o email <span className="font-semibold text-zinc-900">{preview.email}</span></> : ""}. Faça login para acessar seus eventos atribuídos.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3 pt-1">
                      <button
                        type="button"
                        onClick={handleGoLogin}
                        className="w-full h-12 rounded-xl text-white text-[14px] font-bold inline-flex items-center justify-center gap-2 active:scale-[0.99] transition-transform"
                        style={{
                          backgroundImage: "linear-gradient(95deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)",
                          boxShadow: "0 12px 32px -10px rgba(107,61,246,0.45)",
                        }}
                        data-testid="staff-invite-go-login-btn"
                      >
                        Entrar
                        <ArrowRight size={15} />
                      </button>

                      <button
                        type="button"
                        onClick={handleForgotPassword}
                        disabled={resetSending || resetSent}
                        className={`w-full h-11 rounded-xl text-[13px] font-semibold inline-flex items-center justify-center gap-2 transition-all border ${resetSent ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50 hover:border-zinc-300'} disabled:opacity-70`}
                        data-testid="staff-invite-reset-password-btn"
                      >
                        {resetSending ? (
                          <>
                            <Loader2 size={14} className="animate-spin" />
                            Enviando…
                          </>
                        ) : resetSent ? (
                          <>
                            <Check size={14} />
                            Email enviado · veja sua caixa
                          </>
                        ) : (
                          <>
                            <Lock size={14} />
                            Esqueci minha senha
                          </>
                        )}
                      </button>
                    </div>

                    <p className="text-center text-[11.5px] text-zinc-400 pt-1">
                      Suas atribuições serão ativadas automaticamente após o login.
                    </p>
                  </div>
                ) : (
                <form onSubmit={handleAccept} className="space-y-4" data-testid="staff-invite-form">
                  {/* Email locked */}
                  <div>
                    <label className="text-[12px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">
                      Email do convite
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        value={preview.email}
                        disabled
                        readOnly
                        className="w-full h-12 px-4 pl-11 rounded-xl bg-zinc-50 border border-zinc-200 text-[14px] text-zinc-700 cursor-not-allowed font-medium"
                        data-testid="staff-invite-email"
                      />
                      <CheckCircle2 size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-500" />
                    </div>
                    <p className="text-[11.5px] text-zinc-400 mt-1.5">
                      Esse é o email autorizado pelo organizador.
                    </p>
                  </div>

                  {/* Name (somente para novos usuários) */}
                  {!preview.already_user && (
                    <div>
                      <label className="text-[12px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">
                        Seu nome completo
                      </label>
                      <div className="relative">
                        <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Como vamos te chamar?"
                          required
                          minLength={2}
                          autoComplete="name"
                          className="w-full h-12 px-4 pl-11 rounded-xl bg-white border border-zinc-200 text-[14px] text-zinc-900 focus:outline-none focus:border-violet-400 focus:ring-4 focus:ring-violet-100 transition-all"
                          data-testid="staff-invite-name"
                        />
                      </div>
                    </div>
                  )}

                  {/* Password */}
                  <div>
                    <label className="text-[12px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">
                      {preview.already_user ? "Sua senha" : "Crie uma senha"}
                    </label>
                    <div className="relative">
                      <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                      <input
                        type={showPwd ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={preview.already_user ? "Digite sua senha" : "Mínimo 6 caracteres"}
                        required
                        minLength={preview.already_user ? 1 : 6}
                        autoComplete={preview.already_user ? "current-password" : "new-password"}
                        className="w-full h-12 px-4 pl-11 pr-11 rounded-xl bg-white border border-zinc-200 text-[14px] text-zinc-900 focus:outline-none focus:border-violet-400 focus:ring-4 focus:ring-violet-100 transition-all"
                        data-testid="staff-invite-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPwd((v) => !v)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                        aria-label={showPwd ? "Ocultar senha" : "Mostrar senha"}
                      >
                        {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-12 rounded-xl text-white text-[14px] font-bold inline-flex items-center justify-center gap-2 disabled:opacity-70 active:scale-[0.99] transition-transform"
                    style={{
                      backgroundImage: "linear-gradient(95deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)",
                      boxShadow: "0 12px 32px -10px rgba(107,61,246,0.45)",
                    }}
                    data-testid="staff-invite-submit"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={15} className="animate-spin" />
                        Entrando…
                      </>
                    ) : (
                      <>
                        {preview.already_user ? "Entrar e ver eventos" : "Aceitar e entrar"}
                        <ArrowRight size={15} />
                      </>
                    )}
                  </button>

                  <p className="text-center text-[11.5px] text-zinc-400 pt-1">
                    Ao continuar, você aceita os termos da Liventy e passa a colaborar nos eventos atribuídos.
                  </p>
                </form>
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
